import { useCallback, useMemo, useRef, useState } from "react";

import type {
  IssueDependencyHardness,
  IssueDependencyType,
  PMIssue,
  PMIssueDependency,
  ScheduleDateChange,
  ScheduleEdgeChanges,
} from "@/domains/pm/types";

/** A dependency the user drew but has not saved. Negative ids keep it keyable. */
export interface StagedEdge {
  /** Local only, always negative, so it cannot collide with a real row id. */
  localId: number;
  predecessor_issue_id: number;
  successor_issue_id: number;
  dep_type: IssueDependencyType;
  lag_days: number;
  hardness: IssueDependencyHardness;
}

export type EdgePatch = Partial<Pick<PMIssueDependency, "dep_type" | "lag_days" | "hardness">>;

export interface StagedSchedule {
  /** Complete new date pairs, keyed by issue. A second edit replaces the first. */
  dates: Map<number, { start: string | null; due: string | null }>;
  adds: StagedEdge[];
  edits: Map<number, EdgePatch>;
  drops: Set<number>;
  /** How many distinct things the user has changed — what the button counts. */
  count: number;
  isDirty: boolean;
}

/**
 * The Gantt's unsaved arrangement.
 *
 * Held as an OVERLAY rather than by mutating the store: a realtime reload can
 * replace `issuesStore.issues` underneath at any moment, and a buffer keyed by
 * issue id survives that where an edited copy of the list would not.
 *
 * Every map is keyed so a second edit to the same thing replaces the first.
 * Dragging one bar five times is one staged change, not five, which is also
 * what makes the count on the Confirm button mean something.
 */
export function useStagedSchedule() {
  const [dates, setDates] = useState<Map<number, { start: string | null; due: string | null }>>(
    new Map()
  );
  const [adds, setAdds] = useState<StagedEdge[]>([]);
  const [edits, setEdits] = useState<Map<number, EdgePatch>>(new Map());
  const [drops, setDrops] = useState<Set<number>>(new Set());
  const nextLocalId = useRef(-1);

  const stageDates = useCallback((issueId: number, start: string | null, due: string | null) => {
    setDates((prev) => {
      const next = new Map(prev);
      next.set(issueId, { start, due });
      return next;
    });
  }, []);

  const stageEdgeAdd = useCallback(
    /** Returns the staged edge's local id — its `id` in the overlay. */
    (edge: Omit<StagedEdge, "localId">): number => {
      const localId = nextLocalId.current--;
      setAdds((prev) => [...prev, { ...edge, localId }]);
      return localId;
    },
    []
  );

  const stageEdgeEdit = useCallback((edgeId: number, patch: EdgePatch) => {
    if (edgeId < 0) {
      // Editing an edge that is itself still staged: fold the change into the
      // pending add rather than recording an update against an id the server
      // has never seen.
      setAdds((prev) => prev.map((e) => (e.localId === edgeId ? { ...e, ...patch } : e)));
      return;
    }
    setEdits((prev) => {
      const next = new Map(prev);
      next.set(edgeId, { ...(next.get(edgeId) ?? {}), ...patch });
      return next;
    });
  }, []);

  const stageEdgeDrop = useCallback((edgeId: number) => {
    if (edgeId < 0) {
      // A staged edge removed before saving never existed. Cancelling it out
      // here is what stops "draw a link, change your mind" leaving a change
      // behind for Confirm to apply.
      setAdds((prev) => prev.filter((e) => e.localId !== edgeId));
      return;
    }
    setDrops((prev) => new Set(prev).add(edgeId));
    setEdits((prev) => {
      if (!prev.has(edgeId)) return prev;
      const next = new Map(prev);
      next.delete(edgeId);
      return next;
    });
  }, []);

  const discard = useCallback(() => {
    setDates(new Map());
    setAdds([]);
    setEdits(new Map());
    setDrops(new Set());
  }, []);

  const staged = useMemo<StagedSchedule>(() => {
    const count = dates.size + adds.length + edits.size + drops.size;
    return { dates, adds, edits, drops, count, isDirty: count > 0 };
  }, [dates, adds, edits, drops]);

  return { staged, stageDates, stageEdgeAdd, stageEdgeEdit, stageEdgeDrop, discard };
}

// ---------------------------------------------------------------------------
// Reading the overlay
// ---------------------------------------------------------------------------

/** An issue as it appears with the staged dates applied. */
export function overlayIssue(issue: PMIssue, staged: StagedSchedule): PMIssue {
  const pending = staged.dates.get(issue.id);
  if (!pending) return issue;
  return { ...issue, start_date: pending.start, due_date: pending.due };
}

export function overlayIssues(issues: PMIssue[], staged: StagedSchedule): PMIssue[] {
  if (staged.dates.size === 0) return issues;
  return issues.map((i) => overlayIssue(i, staged));
}

/**
 * The dependency list as it appears with the staged changes applied. Staged
 * additions carry their negative localId as `id`, so the arrow layer and the
 * panel can key and delete them exactly like real ones.
 */
export function overlayDependencies(
  dependencies: PMIssueDependency[],
  staged: StagedSchedule
): PMIssueDependency[] {
  const kept = dependencies
    .filter((d) => !staged.drops.has(d.id))
    .map((d) => {
      const patch = staged.edits.get(d.id);
      return patch ? { ...d, ...patch } : d;
    });
  const added: PMIssueDependency[] = staged.adds.map((e) => ({
    id: e.localId,
    predecessor_issue_id: e.predecessor_issue_id,
    successor_issue_id: e.successor_issue_id,
    dep_type: e.dep_type,
    lag_days: e.lag_days,
    hardness: e.hardness,
  }));
  return [...kept, ...added];
}

/** Has this particular issue or edge been touched? Drives the pending styling. */
export function isIssueStaged(issueId: number, staged: StagedSchedule): boolean {
  return staged.dates.has(issueId);
}

export function isEdgeStaged(edgeId: number, staged: StagedSchedule): boolean {
  return edgeId < 0 || staged.edits.has(edgeId);
}

// ---------------------------------------------------------------------------
// Handing the buffer to the server
// ---------------------------------------------------------------------------

export function toDateChanges(staged: StagedSchedule): ScheduleDateChange[] {
  return [...staged.dates.entries()].map(([issue_id, { start, due }]) => ({
    issue_id,
    start_date: start,
    due_date: due,
  }));
}

/** Null when nothing about the graph changed, which the RPCs read as "no overlay". */
export function toEdgeChanges(staged: StagedSchedule): ScheduleEdgeChanges | null {
  if (staged.adds.length === 0 && staged.edits.size === 0 && staged.drops.size === 0) {
    return null;
  }
  return {
    add: staged.adds.map(({ localId: _localId, ...edge }) => edge),
    update: [...staged.edits.entries()].map(([id, patch]) => ({ id, ...patch })),
    remove: [...staged.drops],
  };
}
